import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
} from "@mui/material";
import { colors, shadows } from "../source/source.styles";
import {
  ArrowLeft,
  Copy,
  Info,
  Target,
  Megaphone,
  DollarSign,
  Bell,
  BarChart3,
  Save,
} from "lucide-react";

const EditSourceDetails = ({ source, onBack, onSave, onViewAnalytics }) => {
  const [formData, setFormData] = useState({
    sourceName: source?.name || "Leads by Facebook",
    sourceType: "Paid Social",
    status: true,
    utmSource: "facebook",
    utmMedium: "cpc",
    webhookUrl: "http://api.crmapp.com/webhooks/fb2adc4kp-83af-k25a",
    monthlyBudget: "150000",
    targetCostPerLead: "450",
    roiGoal: "350",
    campaignName: "Annual_LeadGen_Global_2024",
    targetAudienceDescription: "Entrepreneurs and small business owners aged 25-45, interested in CRM software and business process automation within the APAC region.",
    budgetOverspendAlert: true,
    conversionDropAlert: true,
  });

  const handleChange = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCopyWebhook = () => {
    navigator.clipboard.writeText(formData.webhookUrl);
  };

  const handleSave = () => {
    if (onSave) {
      onSave(formData);
    }
    onBack();
  };

  const handleDiscard = () => {
    onBack();
  };

  return (
    <Box sx={{ bgcolor: colors.bgPrimary, minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          borderBottom: `1px solid ${colors.border}`,
          p: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            onClick={onBack}
            sx={{
              color: colors.textSecondary,
              "&:hover": {
                bgcolor: colors.bgPrimary,
              },
            }}
          >
            <ArrowLeft size={20} />
          </IconButton>

          <Stack spacing={0.5} flex={1}>
            {/* Breadcrumb */}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography
                component="span"
                onClick={onBack}
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                  cursor: "pointer",
                  "&:hover": { color: colors.primary },
                }}
              >
                REPORTS
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                }}
              >
                ›
              </Typography>
              <Typography
                component="span"
                onClick={onBack}
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                  cursor: "pointer",
                  "&:hover": { color: colors.primary },
                }}
              >
                SOURCES
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                }}
              >
                ›
              </Typography>
              <Typography
                component="span"
                onClick={onBack}
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                  cursor: "pointer",
                  "&:hover": { color: colors.primary },
                }}
              >
                {source?.name?.toUpperCase() || "LEADS BY FACEBOOK"}
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                }}
              >
                ›
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.primary,
                  letterSpacing: 0.5,
                }}
              >
                EDIT
              </Typography>
            </Stack>

            {/* Title */}
            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 700,
                color: colors.textPrimary,
              }}
            >
              Edit Source Details
            </Typography>
          </Stack>

          <Button
            variant="text"
            startIcon={<BarChart3 size={16} />}
            onClick={() => onViewAnalytics && onViewAnalytics(source)}
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: colors.primary,
              textTransform: "none",
              "&:hover": {
                bgcolor: colors.bgPrimary,
              },
            }}
          >
            View Live Analytics
          </Button>
        </Stack>
      </Box>

      {/* Form Content */}
      <Box sx={{ p: 4 }}>
        <Stack direction="row" spacing={3}>
          {/* Left Column */}
          <Box sx={{ flex: 1 }}>
            {/* General Information */}
            <Box
              sx={{
                bgcolor: "#FFFFFF",
                borderRadius: 3,
                p: 3,
                border: `1px solid ${colors.borderLight}`,
                boxShadow: shadows.card,
                mb: 3,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                <Info size={18} color={colors.primary} />
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: colors.textPrimary }}>
                  General Information
                </Typography>
              </Stack>

              <Stack direction="row" spacing={2} mb={3}>
                <TextField
                  label="SOURCE NAME"
                  value={formData.sourceName}
                  onChange={handleChange("sourceName")}
                  fullWidth
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.textMuted,
                    },
                  }}
                />
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: 11, fontWeight: 600 }}>SOURCE TYPE</InputLabel>
                  <Select
                    value={formData.sourceType}
                    onChange={handleChange("sourceType")}
                    label="SOURCE TYPE"
                  >
                    <MenuItem value="Paid Social">Paid Social</MenuItem>
                    <MenuItem value="Organic">Organic</MenuItem>
                    <MenuItem value="Email">Email</MenuItem>
                    <MenuItem value="Referral">Referral</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary }}>
                  Status
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Switch
                    checked={formData.status}
                    onChange={handleChange("status")}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: colors.primary,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        bgcolor: colors.primary,
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: formData.status ? colors.success : colors.textMuted,
                    }}
                  >
                    {formData.status ? "Active" : "Inactive"}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            {/* Tracking & Attribution */}
            <Box
              sx={{
                bgcolor: "#FFFFFF",
                borderRadius: 3,
                p: 3,
                border: `1px solid ${colors.borderLight}`,
                boxShadow: shadows.card,
                mb: 3,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                <Target size={18} color={colors.primary} />
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: colors.textPrimary }}>
                  Tracking & Attribution
                </Typography>
              </Stack>

              <Stack spacing={3}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="UTM SOURCE"
                    value={formData.utmSource}
                    onChange={handleChange("utmSource")}
                    fullWidth
                    sx={{
                      "& .MuiInputLabel-root": {
                        fontSize: 11,
                        fontWeight: 600,
                        color: colors.textMuted,
                      },
                    }}
                  />
                  <TextField
                    label="UTM MEDIUM"
                    value={formData.utmMedium}
                    onChange={handleChange("utmMedium")}
                    fullWidth
                    sx={{
                      "& .MuiInputLabel-root": {
                        fontSize: 11,
                        fontWeight: 600,
                        color: colors.textMuted,
                      },
                    }}
                  />
                </Stack>

                <Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.textMuted,
                      mb: 1,
                    }}
                  >
                    WEBHOOK URL
                  </Typography>
                  <TextField
                    value={formData.webhookUrl}
                    onChange={handleChange("webhookUrl")}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleCopyWebhook} size="small">
                            <Copy size={16} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Typography sx={{ fontSize: 11, color: colors.textMuted, mt: 0.5 }}>
                    Use this unique webhook to push leads from your lead source directly into CRM.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Campaign Details */}
            <Box
              sx={{
                bgcolor: "#FFFFFF",
                borderRadius: 3,
                p: 3,
                border: `1px solid ${colors.borderLight}`,
                boxShadow: shadows.card,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                <Megaphone size={18} color={colors.primary} />
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: colors.textPrimary }}>
                  Campaign Details
                </Typography>
              </Stack>

              <Stack spacing={3}>
                <TextField
                  label="CAMPAIGN NAME"
                  value={formData.campaignName}
                  onChange={handleChange("campaignName")}
                  fullWidth
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.textMuted,
                    },
                  }}
                />

                <TextField
                  label="TARGET AUDIENCE DESCRIPTION"
                  value={formData.targetAudienceDescription}
                  onChange={handleChange("targetAudienceDescription")}
                  multiline
                  rows={3}
                  fullWidth
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.textMuted,
                    },
                  }}
                />
              </Stack>
            </Box>
          </Box>

          {/* Right Column */}
          <Box sx={{ width: 400 }}>
            {/* Budget & ROI */}
            <Box
              sx={{
                bgcolor: "#FFFFFF",
                borderRadius: 3,
                p: 3,
                border: `1px solid ${colors.borderLight}`,
                boxShadow: shadows.card,
                mb: 3,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                <DollarSign size={18} color={colors.primary} />
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: colors.textPrimary }}>
                  Budget & ROI
                </Typography>
              </Stack>

              <Stack spacing={3}>
                <TextField
                  label="MONTHLY BUDGET (₹)"
                  value={formData.monthlyBudget}
                  onChange={handleChange("monthlyBudget")}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.textMuted,
                    },
                  }}
                />

                <TextField
                  label="TARGET COST PER LEAD (₹)"
                  value={formData.targetCostPerLead}
                  onChange={handleChange("targetCostPerLead")}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.textMuted,
                    },
                  }}
                />

                <TextField
                  label="ROI GOAL (%)"
                  value={formData.roiGoal}
                  onChange={handleChange("roiGoal")}
                  fullWidth
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.textMuted,
                    },
                  }}
                />
              </Stack>
            </Box>

            {/* Alerts */}
            <Box
              sx={{
                bgcolor: "#FFFFFF",
                borderRadius: 3,
                p: 3,
                border: `1px solid ${colors.borderLight}`,
                boxShadow: shadows.card,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                <Bell size={18} color={colors.primary} />
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: colors.textPrimary }}>
                  Alerts
                </Typography>
              </Stack>

              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary }}>
                    Budget Overspend Alert
                  </Typography>
                  <Switch
                    checked={formData.budgetOverspendAlert}
                    onChange={handleChange("budgetOverspendAlert")}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: colors.primary,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        bgcolor: colors.primary,
                      },
                    }}
                  />
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary }}>
                    Conversion Drop Alert
                  </Typography>
                  <Switch
                    checked={formData.conversionDropAlert}
                    onChange={handleChange("conversionDropAlert")}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: colors.primary,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        bgcolor: colors.primary,
                      },
                    }}
                  />
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Stack>

        {/* Action Buttons */}
        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={4}>
          <Button
            variant="outlined"
            onClick={handleDiscard}
            sx={{
              px: 3,
              py: 1,
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              borderColor: colors.border,
              color: colors.textPrimary,
              "&:hover": {
                borderColor: colors.border,
                bgcolor: colors.bgPrimary,
              },
            }}
          >
            Discard Changes
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            startIcon={<Save size={16} />}
            sx={{
              px: 3,
              py: 1,
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              bgcolor: colors.primary,
              color: "#FFFFFF",
              boxShadow: "none",
              "&:hover": {
                bgcolor: colors.primaryDark,
                boxShadow: "none",
              },
            }}
          >
            Save Source Details
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default EditSourceDetails;
